import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../config/supabase';
import './LandingPage.css';

const LandingPage = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeFeature, setActiveFeature] = useState(0);
    const heroRef = useRef(null);

    useEffect(() => {
        const checkAuth = async () => {
            const isLocalAuth = localStorage.getItem('isAuthenticated') === 'true';
            if (isLocalAuth) { setIsAuthenticated(true); return; }
            const { data: { session } } = await supabase.auth.getSession();
            setIsAuthenticated(!!session);
        };
        checkAuth();

        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);

        const observerOptions = { threshold: 0.1, rootMargin: '0px 0px -50px 0px' };
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('lp-visible');
                }
            });
        }, observerOptions);

        document.querySelectorAll('.lp-animate').forEach(el => observer.observe(el));

        const featureInterval = setInterval(() => {
            setActiveFeature(prev => (prev + 1) % 4);
        }, 3000);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            observer.disconnect();
            clearInterval(featureInterval);
        };
    }, []);

    const features = [
        {
            id: 'routing',
            icon: (
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
                </svg>
            ),
            label: 'EPCIS Auto-Routing',
            title: 'Intelligent EPCIS File Routing',
            description: 'Automatically route EPCIS files to the right trading partners based on GLN/SGLN identifiers. Zero manual intervention — our engine reads the event data and delivers it precisely.',
            stats: [
                { value: '< 2s', label: 'Routing latency' },
                { value: '99.9%', label: 'Delivery accuracy' },
                { value: '∞', label: 'File volume' },
            ],
            visual: 'routing',
        },
        {
            id: 'partners',
            icon: (
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            ),
            label: 'Partner Connections',
            title: 'Effortless Partner Onboarding',
            description: 'Set up secure AS2 and SFTP connections with trading partners in minutes. Manage certificates, endpoints, and routing rules from a single intuitive dashboard.',
            stats: [
                { value: '5 min', label: 'Setup time' },
                { value: 'AS2 + SFTP', label: 'Protocols' },
                { value: 'TLS 1.3', label: 'Encryption' },
            ],
            visual: 'partners',
        },
        {
            id: 'analytics',
            icon: (
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
            ),
            label: 'Analytics',
            title: 'Self-Serve Analytics Engine',
            description: 'Eliminate expensive SOP-driven reports. Query your serialization data in real-time — track lot movements, verify product provenance, and detect anomalies instantly.',
            stats: [
                { value: 'Real-time', label: 'Data freshness' },
                { value: 'Custom', label: 'Dashboards' },
                { value: 'Export', label: 'CSV / JSON' },
            ],
            visual: 'analytics',
        },
        {
            id: 'as2',
            icon: (
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
            ),
            label: 'AS2 Routing Metrics',
            title: 'Full AS2 Transmission Visibility',
            description: 'Monitor every AS2 message from send to MDN acknowledgment. Track delivery rates, retry attempts, and failure patterns with granular per-partner metrics.',
            stats: [
                { value: 'MDN', label: 'Verified receipts' },
                { value: '30d', label: 'History window' },
                { value: 'Alerts', label: 'Failure detection' },
            ],
            visual: 'as2',
        },
    ];

    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

    return (
        <div className="lp-root">
            {/* ── Navbar ── */}
            <nav className={`lp-nav ${scrolled ? 'lp-nav--scrolled' : ''}`}>
                <div className="lp-nav__inner">
                    <a href="#" className="lp-logo">
                        <span className="lp-logo__icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"
                                    d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        </span>
                        <span>Titan<span className="lp-logo__accent">Trace</span></span>
                    </a>

                    <ul className={`lp-nav__links ${isMobileMenuOpen ? 'lp-nav__links--open' : ''}`}>
                        <li><a href="#autorouting">EPCIS Routing</a></li>
                        <li><a href="#partners">Partners</a></li>
                        <li><a href="#analytics">Analytics</a></li>
                        <li><a href="#as2">AS2 Metrics</a></li>
                        <li>
                            <Link to="/login" className="lp-btn lp-btn--outline">
                                {isAuthenticated ? 'Go to Dashboard' : 'Sign In'}
                            </Link>
                        </li>
                        <li>
                            <Link to="/login" className="lp-btn lp-btn--primary">
                                Get Started
                            </Link>
                        </li>
                    </ul>

                    <button className="lp-nav__hamburger" onClick={toggleMobileMenu} aria-label="Toggle navigation">
                        <span className={`lp-bar ${isMobileMenuOpen ? 'lp-bar--top' : ''}`}></span>
                        <span className={`lp-bar ${isMobileMenuOpen ? 'lp-bar--mid' : ''}`}></span>
                        <span className={`lp-bar ${isMobileMenuOpen ? 'lp-bar--bot' : ''}`}></span>
                    </button>
                </div>
            </nav>

            {/* ── Hero ── */}
            <section className="lp-hero" ref={heroRef}>
                <div className="lp-hero__noise"></div>
                <div className="lp-hero__glow lp-hero__glow--purple"></div>
                <div className="lp-hero__glow lp-hero__glow--teal"></div>
                <div className="lp-hero__glow lp-hero__glow--mid"></div>

                <div className="lp-container lp-hero__inner">
                    <div className="lp-hero__badge lp-animate">
                        <span className="lp-badge__dot"></span>
                        GS1 EPCIS 2.0 · AS2 · DSCSA Compliant
                    </div>

                    <h1 className="lp-hero__headline lp-animate">
                        Supply Chain Visibility
                        <br />
                        <span className="lp-gradient-text">Without the Complexity</span>
                    </h1>

                    <p className="lp-hero__sub lp-animate">
                        Titan Trace sits on top of your existing L4 system — automatically routing EPCIS files,
                        connecting trading partners via AS2, and delivering real-time analytics that used to require
                        expensive, multi-week SOPs.
                    </p>

                    <div className="lp-hero__ctas lp-animate">
                        <Link to="/login" className="lp-btn lp-btn--hero">
                            Start Free Trial
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </Link>
                        <a href="#autorouting" className="lp-btn lp-btn--ghost">
                            See how it works
                        </a>
                    </div>

                    <div className="lp-hero__stats lp-animate">
                        <div className="lp-stat">
                            <span className="lp-stat__value">99.9%</span>
                            <span className="lp-stat__label">Uptime SLA</span>
                        </div>
                        <div className="lp-stat__sep"></div>
                        <div className="lp-stat">
                            <span className="lp-stat__value">&lt; 2s</span>
                            <span className="lp-stat__label">Routing Latency</span>
                        </div>
                        <div className="lp-stat__sep"></div>
                        <div className="lp-stat">
                            <span className="lp-stat__value">DSCSA</span>
                            <span className="lp-stat__label">Ready</span>
                        </div>
                        <div className="lp-stat__sep"></div>
                        <div className="lp-stat">
                            <span className="lp-stat__value">GS1</span>
                            <span className="lp-stat__label">Compliant</span>
                        </div>
                    </div>

                    {/* 3D Dashboard card */}
                    <div className="lp-hero__mockup lp-animate">
                        <div className="lp-hero__mockup-frame">
                            <div className="lp-mockup-bar">
                                <span className="lp-mockup-dot lp-mockup-dot--red"></span>
                                <span className="lp-mockup-dot lp-mockup-dot--yellow"></span>
                                <span className="lp-mockup-dot lp-mockup-dot--green"></span>
                                <span className="lp-mockup-url">app.titantrace.io / dashboard</span>
                            </div>
                            <div className="lp-mockup-body">
                                <div className="lp-mockup-sidebar">
                                    {['Dashboard', 'EPCIS Routing', 'Partners', 'AS2 Metrics', 'Analytics', 'File Tx'].map((item, i) => (
                                        <div key={i} className={`lp-mockup-nav-item ${i === 1 ? 'lp-mockup-nav-item--active' : ''}`}>
                                            <span className="lp-mockup-nav-dot"></span>
                                            {item}
                                        </div>
                                    ))}
                                </div>
                                <div className="lp-mockup-content">
                                    <div className="lp-mockup-header-row">
                                        <div>
                                            <div className="lp-mockup-title">EPCIS Routing</div>
                                            <div className="lp-mockup-sub">Last 24 hours · Auto-refresh</div>
                                        </div>
                                        <div className="lp-mockup-badge-green">● Live</div>
                                    </div>
                                    <div className="lp-mockup-metrics">
                                        {[
                                            { label: 'Files Routed', value: '2,847', change: '+12%' },
                                            { label: 'Partners Active', value: '34', change: '+3' },
                                            { label: 'Success Rate', value: '99.8%', change: '↑' },
                                            { label: 'Avg Latency', value: '1.4s', change: '↓ 0.2s' },
                                        ].map((m, i) => (
                                            <div key={i} className="lp-mockup-metric">
                                                <div className="lp-mockup-metric-val">{m.value}</div>
                                                <div className="lp-mockup-metric-lbl">{m.label}</div>
                                                <div className="lp-mockup-metric-chg">{m.change}</div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="lp-mockup-chart">
                                        {[65, 80, 55, 90, 75, 95, 70, 85, 60, 92, 78, 88].map((h, i) => (
                                            <div key={i} className="lp-mockup-bar-wrap">
                                                <div className="lp-mockup-chart-bar" style={{ height: `${h}%` }}></div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="lp-mockup-table-header">Recent Transmissions</div>
                                    {[
                                        { partner: 'Trading Partner A', file: 'shipment_2847.xml', status: 'Delivered', time: '2s ago' },
                                        { partner: 'Trading Partner B', file: 'epcis_lot_229.xml', status: 'Delivered', time: '18s ago' },
                                        { partner: 'Trading Partner C', file: 'trace_event_41.xml', status: 'Routing...', time: 'now' },
                                    ].map((row, i) => (
                                        <div key={i} className="lp-mockup-table-row">
                                            <span className="lp-mockup-partner">{row.partner}</span>
                                            <span className="lp-mockup-file">{row.file}</span>
                                            <span className={`lp-mockup-status ${row.status === 'Delivered' ? 'lp-mockup-status--ok' : 'lp-mockup-status--pending'}`}>{row.status}</span>
                                            <span className="lp-mockup-time">{row.time}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Trusted By ── */}
            <section className="lp-trusted">
                <div className="lp-container">
                    <p className="lp-trusted__label">Built for trading partner networks of any size</p>
                    <div className="lp-trusted__logos">
                        {['Trading Partner A', 'Trading Partner B', 'Trading Partner C', 'Trading Partner D', 'Trading Partner E', 'Trading Partner F'].map((name, i) => (
                            <div key={i} className="lp-trusted__logo">{name}</div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Feature Tabs ── */}
            <section className="lp-features-overview">
                <div className="lp-container">
                    <div className="lp-section-eyebrow lp-animate">Platform Capabilities</div>
                    <h2 className="lp-section-title lp-animate">
                        Everything your L4 needs.<br />
                        <span className="lp-gradient-text">Nothing it doesn't.</span>
                    </h2>
                    <p className="lp-section-sub lp-animate">
                        Drop Titan Trace on top of your existing infrastructure. No migration, no downtime — just
                        immediate capability expansion.
                    </p>

                    <div className="lp-feature-tabs lp-animate">
                        {features.map((f, i) => (
                            <button
                                key={f.id}
                                className={`lp-feature-tab ${activeFeature === i ? 'lp-feature-tab--active' : ''}`}
                                onClick={() => setActiveFeature(i)}
                            >
                                <span className="lp-feature-tab__icon">{f.icon}</span>
                                {f.label}
                            </button>
                        ))}
                    </div>

                    <div className="lp-feature-panel lp-animate">
                        <div className="lp-feature-panel__info">
                            <h3>{features[activeFeature].title}</h3>
                            <p>{features[activeFeature].description}</p>
                            <div className="lp-feature-stats">
                                {features[activeFeature].stats.map((s, i) => (
                                    <div key={i} className="lp-feature-stat">
                                        <span className="lp-feature-stat__value">{s.value}</span>
                                        <span className="lp-feature-stat__label">{s.label}</span>
                                    </div>
                                ))}
                            </div>
                            <Link to="/login" className="lp-btn lp-btn--primary lp-btn--sm">
                                Explore this feature →
                            </Link>
                        </div>
                        <div className={`lp-feature-panel__visual lp-visual--${features[activeFeature].visual}`}>
                            <FeatureVisual type={features[activeFeature].visual} />
                        </div>
                    </div>
                </div>
            </section>

            {/* ── EPCIS Auto-Routing Deep Dive ── */}
            <section id="autorouting" className="lp-section lp-section--dark">
                <div className="lp-section__noise"></div>
                <div className="lp-container">
                    <div className="lp-split">
                        <div className="lp-split__text lp-animate">
                            <div className="lp-section-eyebrow lp-section-eyebrow--light">EPCIS Auto-Routing</div>
                            <h2 className="lp-section-title lp-section-title--light">
                                Files route themselves.<br />
                                <span className="lp-gradient-text">You just watch.</span>
                            </h2>
                            <p className="lp-split__body">
                                When an EPCIS file arrives, Titan Trace parses the event data, extracts the GLN and SGLN
                                identifiers, and instantly matches them against your configured routing rules. The right
                                file reaches the right partner — every time, automatically.
                            </p>
                            <ul className="lp-check-list">
                                <li>Supports EPCIS 1.2 and 2.0 XML/JSON-LD formats</li>
                                <li>Route by GLN, SGLN, event type, or business step</li>
                                <li>Fallback rules for unmatched events</li>
                                <li>Full audit trail for every routing decision</li>
                                <li>DSCSA serialization event compliance built-in</li>
                            </ul>
                            <Link to="/login" className="lp-btn lp-btn--primary lp-btn--lg">
                                Set up auto-routing →
                            </Link>
                        </div>

                        <div className="lp-split__visual lp-animate">
                            <RoutingDiagram />
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Partner Connections ── */}
            <section id="partners" className="lp-section lp-section--light">
                <div className="lp-container">
                    <div className="lp-split lp-split--reverse">
                        <div className="lp-split__visual lp-animate">
                            <PartnerVisual />
                        </div>
                        <div className="lp-split__text lp-animate">
                            <div className="lp-section-eyebrow">Partner Connections</div>
                            <h2 className="lp-section-title">
                                One dashboard.<br />
                                <span className="lp-gradient-text">Every partner.</span>
                            </h2>
                            <p className="lp-split__body">
                                Onboard trading partners in minutes, not weeks. Configure AS2 endpoints, upload
                                certificates, set routing rules, and test connectivity — all from a single screen.
                                No EDI expertise required.
                            </p>
                            <ul className="lp-check-list">
                                <li>AS2 and SFTP connection management</li>
                                <li>Certificate lifecycle management</li>
                                <li>Auto-complete partner SGLN lookup</li>
                                <li>Connection health monitoring</li>
                                <li>Bulk partner import via CSV</li>
                            </ul>
                            <Link to="/login" className="lp-btn lp-btn--primary lp-btn--lg">
                                Connect your first partner →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Analytics ── */}
            <section id="analytics" className="lp-section lp-section--dark">
                <div className="lp-section__noise"></div>
                <div className="lp-container">
                    <div className="lp-centered lp-animate">
                        <div className="lp-section-eyebrow lp-section-eyebrow--light">Analytics</div>
                        <h2 className="lp-section-title lp-section-title--light">
                            Insights that used to take weeks.<br />
                            <span className="lp-gradient-text">Available in seconds.</span>
                        </h2>
                        <p className="lp-section-sub lp-section-sub--light">
                            Stop waiting on IT to run reports. Titan Trace puts your serialization data directly in your
                            hands with filterable, exportable dashboards.
                        </p>
                    </div>

                    <div className="lp-analytics-grid">
                        {[
                            {
                                icon: '📦',
                                title: 'EPCIS Event Explorer',
                                desc: 'Search, filter, and drill into every EPCIS event. Filter by GLN, SGLN, lot number, or event type across any date range.',
                                tag: 'Real-time',
                            },
                            {
                                icon: '📊',
                                title: 'Transmission Reports',
                                desc: 'Track file delivery rates by partner, time period, and file type. Spot bottlenecks before they become compliance issues.',
                                tag: 'Exportable',
                            },
                            {
                                icon: '🔍',
                                title: 'LOT & PO Tracing',
                                desc: 'Instantly trace any lot number or purchase order across your entire supply chain — from manufacturer to dispenser.',
                                tag: 'Instant',
                            },
                            {
                                icon: '⚡',
                                title: 'Anomaly Detection',
                                desc: 'Automated alerts when routing failures, MDN errors, or unusual transmission patterns are detected.',
                                tag: 'Automated',
                            },
                            {
                                icon: '✅',
                                title: 'EPCIS Validator',
                                desc: 'Validate EPCIS files against GS1 specifications before transmission. Catch errors before they reach your partners.',
                                tag: 'GS1 Spec',
                            },
                            {
                                icon: '📋',
                                title: 'Compliance Audit Trail',
                                desc: 'Every event, every routing decision, every transmission — permanently logged for DSCSA audit readiness.',
                                tag: 'DSCSA',
                            },
                        ].map((card, i) => (
                            <div key={i} className="lp-analytics-card lp-animate" style={{ animationDelay: `${i * 80}ms` }}>
                                <div className="lp-analytics-card__tag">{card.tag}</div>
                                <div className="lp-analytics-card__icon">{card.icon}</div>
                                <h3>{card.title}</h3>
                                <p>{card.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── AS2 Metrics ── */}
            <section id="as2" className="lp-section lp-section--light">
                <div className="lp-container">
                    <div className="lp-split">
                        <div className="lp-split__text lp-animate">
                            <div className="lp-section-eyebrow">AS2 Routing Metrics</div>
                            <h2 className="lp-section-title">
                                Complete AS2 visibility.<br />
                                <span className="lp-gradient-text">From send to MDN.</span>
                            </h2>
                            <p className="lp-split__body">
                                Monitor every AS2 transmission in real time. Track MDN acknowledgments, identify failed
                                deliveries, and diagnose retry patterns across all your trading partner connections —
                                before your partners call you.
                            </p>
                            <ul className="lp-check-list">
                                <li>Per-partner transmission dashboards</li>
                                <li>MDN receipt tracking and verification</li>
                                <li>Retry queue monitoring</li>
                                <li>File-level delivery confirmations</li>
                                <li>Historical metrics with 30-day window</li>
                            </ul>
                            <Link to="/login" className="lp-btn lp-btn--primary lp-btn--lg">
                                View AS2 metrics →
                            </Link>
                        </div>

                        <div className="lp-split__visual lp-animate">
                            <AS2Visual />
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CTA Banner ── */}
            <section className="lp-cta">
                <div className="lp-cta__glow"></div>
                <div className="lp-container lp-cta__inner">
                    <div className="lp-animate">
                        <div className="lp-section-eyebrow lp-section-eyebrow--light">Get Started Today</div>
                        <h2 className="lp-cta__headline">
                            Your L4 system deserves<br />
                            <span className="lp-gradient-text">a smarter layer on top.</span>
                        </h2>
                        <p className="lp-cta__sub">
                            No migration required. No new infrastructure. Titan Trace connects to what you already have
                            and makes it dramatically more powerful.
                        </p>
                        <div className="lp-cta__actions">
                            <Link to="/login" className="lp-btn lp-btn--hero">
                                Start Free Trial
                                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </Link>
                            <a href="mailto:sales@titantrace.io" className="lp-btn lp-btn--ghost">
                                Talk to Sales
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Footer ── */}
            <footer className="lp-footer">
                <div className="lp-container lp-footer__inner">
                    <div className="lp-footer__brand">
                        <a href="#" className="lp-logo lp-logo--footer">
                            <span className="lp-logo__icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </span>
                            <span>Titan<span className="lp-logo__accent">Trace</span></span>
                        </a>
                        <p className="lp-footer__tagline">
                            Intelligent supply chain visibility for pharmaceutical and healthcare distributors.
                        </p>
                        <p className="lp-footer__copy">© 2025 Titan Trace. All rights reserved.</p>
                    </div>

                    <div className="lp-footer__links">
                        <div className="lp-footer__col">
                            <h4>Platform</h4>
                            <a href="#autorouting">EPCIS Auto-Routing</a>
                            <a href="#partners">Partner Connections</a>
                            <a href="#analytics">Analytics</a>
                            <a href="#as2">AS2 Metrics</a>
                        </div>
                        <div className="lp-footer__col">
                            <h4>Compliance</h4>
                            <a href="#">DSCSA Readiness</a>
                            <a href="#">GS1 EPCIS 2.0</a>
                            <a href="#">Audit Trails</a>
                            <a href="#">Data Security</a>
                        </div>
                        <div className="lp-footer__col">
                            <h4>Company</h4>
                            <a href="#">About Us</a>
                            <a href="#">Contact</a>
                            <a href="mailto:sales@titantrace.io">Sales</a>
                            <Link to="/login">Sign In</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

/* ── Inline visual components ── */

const RoutingDiagram = () => (
    <div className="lp-routing-diagram">
        <div className="lp-rd-card lp-rd-card--source">
            <div className="lp-rd-card-label">EPCIS File Received</div>
            <div className="lp-rd-card-detail">shipment_epcis_2024.xml</div>
            <div className="lp-rd-card-meta">GLN: 0345678901234</div>
        </div>
        <div className="lp-rd-arrow">
            <div className="lp-rd-arrow-line"></div>
            <div className="lp-rd-arrow-pulse"></div>
        </div>
        <div className="lp-rd-engine">
            <div className="lp-rd-engine-label">Routing Engine</div>
            <div className="lp-rd-rule">Match: SGLN → Partner A</div>
            <div className="lp-rd-rule lp-rd-rule--dim">Rule: GLN prefix 034</div>
        </div>
        <div className="lp-rd-arrow">
            <div className="lp-rd-arrow-line"></div>
            <div className="lp-rd-arrow-pulse lp-rd-arrow-pulse--delay"></div>
        </div>
        <div className="lp-rd-partners">
            <div className="lp-rd-partner lp-rd-partner--active">
                <div className="lp-rd-partner-dot"></div>
                <div>
                    <div className="lp-rd-partner-name">Trading Partner A</div>
                    <div className="lp-rd-partner-status">✓ Delivered · 1.4s</div>
                </div>
            </div>
            <div className="lp-rd-partner">
                <div className="lp-rd-partner-dot lp-rd-partner-dot--dim"></div>
                <div>
                    <div className="lp-rd-partner-name">Trading Partner B</div>
                    <div className="lp-rd-partner-status lp-rd-partner-status--dim">Awaiting next batch</div>
                </div>
            </div>
            <div className="lp-rd-partner">
                <div className="lp-rd-partner-dot lp-rd-partner-dot--dim"></div>
                <div>
                    <div className="lp-rd-partner-name">Trading Partner C</div>
                    <div className="lp-rd-partner-status lp-rd-partner-status--dim">Awaiting next batch</div>
                </div>
            </div>
        </div>
    </div>
);

const PartnerVisual = () => (
    <div className="lp-partner-visual">
        <div className="lp-pv-header">
            <span className="lp-pv-title">Partner Configuration</span>
            <span className="lp-pv-badge">AS2 Active</span>
        </div>
        <div className="lp-pv-body">
            {[
                { name: 'Trading Partner A', id: '0345678901', status: 'connected', protocol: 'AS2' },
                { name: 'Trading Partner B', id: '0987654321', status: 'connected', protocol: 'AS2' },
                { name: 'Trading Partner C', id: '0112233445', status: 'pending', protocol: 'SFTP' },
                { name: 'Trading Partner D', id: '0556677889', status: 'connected', protocol: 'AS2' },
            ].map((p, i) => (
                <div key={i} className="lp-pv-row">
                    <div className={`lp-pv-status ${p.status === 'connected' ? 'lp-pv-status--ok' : 'lp-pv-status--pending'}`}></div>
                    <div className="lp-pv-info">
                        <span className="lp-pv-name">{p.name}</span>
                        <span className="lp-pv-id">GLN: {p.id}</span>
                    </div>
                    <span className="lp-pv-protocol">{p.protocol}</span>
                    <span className={`lp-pv-badge-sm ${p.status === 'connected' ? 'lp-pv-badge-sm--ok' : 'lp-pv-badge-sm--pending'}`}>
                        {p.status}
                    </span>
                </div>
            ))}
        </div>
        <div className="lp-pv-footer">
            <button className="lp-pv-add-btn">+ Add Partner</button>
        </div>
    </div>
);

const AS2Visual = () => (
    <div className="lp-as2-visual">
        <div className="lp-as2-header">
            <span className="lp-as2-title">AS2 Transmission Monitor</span>
            <span className="lp-as2-live">● Live</span>
        </div>
        <div className="lp-as2-metrics">
            {[
                { label: 'Sent Today', value: '847', color: 'blue' },
                { label: 'MDN Received', value: '843', color: 'green' },
                { label: 'Retrying', value: '3', color: 'yellow' },
                { label: 'Failed', value: '1', color: 'red' },
            ].map((m, i) => (
                <div key={i} className={`lp-as2-metric lp-as2-metric--${m.color}`}>
                    <div className="lp-as2-metric-val">{m.value}</div>
                    <div className="lp-as2-metric-lbl">{m.label}</div>
                </div>
            ))}
        </div>
        <div className="lp-as2-chart">
            {[40, 65, 50, 80, 70, 90, 75, 85, 60, 95, 80, 88, 72, 91].map((h, i) => (
                <div key={i} className="lp-as2-bar-wrap">
                    <div className="lp-as2-bar" style={{ height: `${h}%` }}></div>
                </div>
            ))}
        </div>
        <div className="lp-as2-transmissions">
            {[
                { id: 'TX-8847', partner: 'Partner A', size: '124 KB', status: 'MDN OK', time: '0:01' },
                { id: 'TX-8846', partner: 'Partner B', size: '89 KB', status: 'MDN OK', time: '0:03' },
                { id: 'TX-8845', partner: 'Partner C', size: '201 KB', status: 'Retrying', time: '0:07' },
            ].map((tx, i) => (
                <div key={i} className="lp-as2-row">
                    <span className="lp-as2-id">{tx.id}</span>
                    <span className="lp-as2-partner">{tx.partner}</span>
                    <span className="lp-as2-size">{tx.size}</span>
                    <span className={`lp-as2-status ${tx.status === 'MDN OK' ? 'lp-as2-status--ok' : 'lp-as2-status--warn'}`}>{tx.status}</span>
                </div>
            ))}
        </div>
    </div>
);

const FeatureVisual = ({ type }) => {
    const visuals = {
        routing: (
            <div className="lp-fv lp-fv--routing">
                <div className="lp-fv-pill lp-fv-pill--green">Auto-matched · GLN 034xxxxx</div>
                <div className="lp-fv-flow">
                    <div className="lp-fv-node">EPCIS In</div>
                    <div className="lp-fv-connector"><div className="lp-fv-pulse"></div></div>
                    <div className="lp-fv-node lp-fv-node--engine">Engine</div>
                    <div className="lp-fv-connector"><div className="lp-fv-pulse lp-fv-pulse--delay"></div></div>
                    <div className="lp-fv-node lp-fv-node--out">Partner</div>
                </div>
                <div className="lp-fv-log">
                    <div className="lp-fv-log-line"><span className="green">✓</span> shipment_2847.xml → Partner A</div>
                    <div className="lp-fv-log-line"><span className="green">✓</span> epcis_lot_229.xml → Cardinal</div>
                    <div className="lp-fv-log-line lp-fv-log-line--active"><span className="yellow">→</span> trace_41.xml routing...</div>
                </div>
            </div>
        ),
        partners: (
            <div className="lp-fv lp-fv--partners">
                <div className="lp-fv-partner-list">
                    {['Trading Partner A • AS2 • ✓', 'Trading Partner B • AS2 • ✓', 'Trading Partner C • SFTP • ✓', 'Trading Partner D • AS2 • …'].map((p, i) => (
                        <div key={i} className={`lp-fv-partner-row ${i === 3 ? 'lp-fv-partner-row--pending' : ''}`}>{p}</div>
                    ))}
                </div>
            </div>
        ),
        analytics: (
            <div className="lp-fv lp-fv--analytics">
                <div className="lp-fv-chart-bars">
                    {[60, 80, 45, 90, 70, 85, 55, 95].map((h, i) => (
                        <div key={i} className="lp-fv-chart-col">
                            <div className="lp-fv-chart-bar" style={{ height: `${h}%` }}></div>
                        </div>
                    ))}
                </div>
                <div className="lp-fv-kpis">
                    <div className="lp-fv-kpi"><div className="lp-fv-kpi-val">2,847</div><div className="lp-fv-kpi-lbl">Events</div></div>
                    <div className="lp-fv-kpi"><div className="lp-fv-kpi-val">99.8%</div><div className="lp-fv-kpi-lbl">Success</div></div>
                </div>
            </div>
        ),
        as2: (
            <div className="lp-fv lp-fv--as2">
                <div className="lp-fv-as2-list">
                    {[
                        { id: 'TX-8847', status: 'MDN OK' },
                        { id: 'TX-8846', status: 'MDN OK' },
                        { id: 'TX-8845', status: 'Retrying' },
                    ].map((tx, i) => (
                        <div key={i} className="lp-fv-as2-row">
                            <span>{tx.id}</span>
                            <span className={tx.status === 'MDN OK' ? 'green' : 'yellow'}>{tx.status}</span>
                        </div>
                    ))}
                </div>
                <div className="lp-fv-as2-stats">
                    <div>847 <span>sent</span></div>
                    <div>843 <span>confirmed</span></div>
                </div>
            </div>
        ),
    };
    return visuals[type] || null;
};

export default LandingPage;
